import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Container, 
  CssBaseline,
  Drawer,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CashFlow from './pages/CashFlow';
import CashFlowPersonal from './pages/CashFlowPersonal';
import Categories from './pages/Categories';
import CategoriesPersonal from './pages/CategoriesPersonal';
import Clients from './pages/Clients';
import Services from './pages/Services';
import PaymentSources from './pages/PaymentSources';
import Installments from './pages/Installments';
import PersonalInstallments from './pages/PersonalInstallments';
import ReportsPersonal from './pages/ReportsPersonal';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MenuIcon from '@mui/icons-material/Menu';
import PieChartIcon from '@mui/icons-material/PieChart';
import theme from './theme';
import { checkSupabaseConnection } from './lib/supabase';
import SecurityModal from './pages/SecurityModal';
import { useNavigate } from 'react-router-dom';

function Navigation({ setIsModalOpen, perfil }) {
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  let menuItems = [];
  if (perfil === "empresa") {
    menuItems = [
      { path: '/cash-flow', label: 'Fluxo de Caixa', icon: <MonetizationOnIcon /> },
      { path: '/clients', label: 'Clientes', icon: <PeopleIcon /> },
      { path: '/services', label: 'Serviços', icon: <AssignmentIcon /> },
      { path: '/categories', label: 'Categorias', icon: <CategoryIcon /> },
      { path: '/installments', label: 'Parcelamentos', icon: <MonetizationOnIcon /> }
    ];
  } else if (perfil === "pessoal") {
    menuItems = [
      { path: '/cash-flow-personal', label: 'Fluxo Pessoal', icon: <MonetizationOnIcon /> },
      { path: '/categories-personal', label: 'Categorias Pessoais', icon: <CategoryIcon /> },
      { path: '/payment-sources', label: 'Origens de Pagamento', icon: <MonetizationOnIcon /> },
      { path: '/personal-installments', label: 'Parcelamentos', icon: <MonetizationOnIcon /> },
      { path: '/reports-personal', label: 'Relatórios', icon: <PieChartIcon /> }
    ];
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuContent = (
    <>
      {menuItems.map((item) => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            onClick={() => setMobileOpen(false)}
            sx={{ 
              px: isMobile ? 2 : 3,
              py: 1.5,
              minHeight: 48,
              color: 'text.secondary',
              position: 'relative',
              transition: 'all 0.2s ease-in-out',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: isMobile ? '8px' : '12px',
                right: isMobile ? '8px' : '12px',
                height: '2px',
                bgcolor: 'transparent',
                transition: 'all 0.2s ease-in-out',
              },
              '&.Mui-selected': {
                backgroundColor: 'transparent',
                color: 'primary.main',
                fontWeight: 600,
                '&::after': {
                  bgcolor: 'primary.main',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                }
              },
              '&:hover': { 
                backgroundColor: 'background.subtle',
                color: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                }
              }
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: isMobile ? 32 : 36,
              color: 'inherit'
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label} 
              primaryTypographyProps={{
                fontSize: isMobile ? '0.875rem' : '0.875rem',
                fontWeight: location.pathname === item.path ? 600 : 500
              }}
            />
          </ListItemButton>
        </ListItem>
      ))}
      
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            setIsModalOpen(true);
            setMobileOpen(false);
          }}
          sx={{ 
            px: isMobile ? 2 : 3,
            py: 1.5,
            minHeight: 48,
            color: 'text.secondary',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary="Sair" />
        </ListItemButton>
      </ListItem>
    </>
  );

  if (isMobile) {
    return (
      <>
        <IconButton
          color="inherit"
          aria-label="abrir menu"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ 
            mr: 2,
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main'
            },
            minWidth: '44px',
            minHeight: '44px'
          }}
        >
          <MenuIcon />
        </IconButton>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 280,
              borderRight: '1px solid',
              borderColor: 'divider',
              paddingBottom: 'env(safe-area-inset-bottom)'
            },
          }}
        >
          <Box sx={{ p: 2, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                fontWeight: 700,
                mb: 3,
                textAlign: 'center'
              }}
            >
              WeFinance
            </Typography>
            <List>
              {menuContent}
            </List>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <List sx={{ 
        display: 'flex',
        m: 0,
        p: 0,
        alignItems: 'center',
        height: '100%'
      }}>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ width: 'auto' }}>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{ 
                px: isTablet ? 2 : 3,
                py: 1.5,
                minHeight: 48,
                color: 'text.secondary',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: isTablet ? '8px' : '12px',
                  right: isTablet ? '8px' : '12px',
                  height: '2px',
                  bgcolor: 'transparent',
                  transition: 'all 0.2s ease-in-out',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  fontWeight: 600,
                  '&::after': {
                    bgcolor: 'primary.main',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  }
                },
                '&:hover': { 
                  backgroundColor: 'background.subtle',
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: isTablet ? 32 : 36,
                color: 'inherit'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{
                  fontSize: isTablet ? '0.8125rem' : '0.875rem',
                  fontWeight: location.pathname === item.path ? 600 : 500,
                  display: { xs: 'none', sm: 'block' }
                }}
                sx={{ display: isTablet ? { sm: 'none', md: 'block' } : 'block' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <List sx={{ display: 'flex', m: 0, p: 0, alignItems: 'center', height: '100%' }}>
        <ListItem disablePadding sx={{ width: 'auto' }}>
          <ListItemButton
            onClick={() => setIsModalOpen(true)}
            sx={{ 
              px: isTablet ? 2 : 3,
              py: 1.5,
              minHeight: 48,
              color: 'text.secondary',
              position: 'relative',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <ListItemIcon sx={{ minWidth: isTablet ? 32 : 36 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Sair" 
              primaryTypographyProps={{
                fontSize: isTablet ? '0.8125rem' : '0.875rem',
                display: { xs: 'none', sm: 'block' }
              }}
              sx={{ display: isTablet ? { sm: 'none', md: 'block' } : 'block' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
}

function AppContent() {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [perfil, setPerfil] = useState(null);
  const navigate = useNavigate();

  const handleSuccess = (perfilSelecionado) => {
    setPerfil(perfilSelecionado);
    setIsModalOpen(false);
    if (perfilSelecionado === "empresa") {
      navigate("/cash-flow");
    } else if (perfilSelecionado === "pessoal") {
      navigate("/cash-flow-personal", { state: { autoFilterCurrentMonth: true } });
    }
  };

  useEffect(() => {
    const testConnection = async () => {
      const isConnected = await checkSupabaseConnection();
      console.log('Status da conexão Supabase:', isConnected);
    };

    testConnection();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SecurityModal open={isModalOpen} onClose={() => {}} onSuccess={handleSuccess} />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        <AppBar 
          position="sticky" 
          sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          }}
          elevation={0}
        >
          <Container maxWidth="lg">
            <Toolbar 
              sx={{ 
                minHeight: { xs: '64px', md: '80px' },
                px: { xs: 2, sm: 4 },
                gap: 2
              }}
              disableGutters
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  mr: { xs: 2, md: 6 },
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}
              >
                WeFinance
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <Navigation setIsModalOpen={setIsModalOpen} perfil={perfil} />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        <Container 
          component="main" 
          maxWidth="lg" 
          sx={{ 
            flexGrow: 1, 
            py: { xs: 3, md: 4 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Routes>
            <Route path="/" element={<CashFlow />} />
            <Route path="/cash-flow" element={<CashFlow />} />
            <Route path="/cash-flow-personal" element={<CashFlowPersonal />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/services" element={<Services />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/categories-personal" element={<CategoriesPersonal />} />
            <Route path="/payment-sources" element={<PaymentSources />} />
            <Route path="/installments" element={<Installments />} />
            <Route path="/personal-installments" element={<PersonalInstallments />} />
            <Route path="/reports-personal" element={<ReportsPersonal />} />
          </Routes>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
